import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from '../lib/supabase';
import { useBreakpoint } from '../hooks/useBreakpoint';
import AppLogo from "./AppLogo";

// AUTH MODEL:
// Personal (client / JiraniPro):
//   User enters phone + password directly.
//   supabase.auth.signInWithPassword({ phone, password }) — real phone auth,
//   no synthetic email involved.
//
// Business:
//   User enters email + password directly.
//   supabase.auth.signInWithPassword({ email, password })
//
// After login we read profiles.role to route:
//   'pro'      -> /pro/dashboard
//   'business' -> /business/account
//   otherwise  -> /
// UNLESS a redirectTo was passed in (e.g. from a "Book Now" button that
// opened the modal mid-flow) - in that case we always honor redirectTo
// instead, so the user lands back where they were, not on a role dashboard.

const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=Instrument+Serif:ital@0;1&display=swap";
document.head.appendChild(fontLink);

const F = "'Plus Jakarta Sans', sans-serif";
const SERIF = "'Instrument Serif', Georgia, serif";

/* Phone normalisation (must match SignUpPage) */
const normalisePhone = (p) => {
  const digits = p.replace(/[^0-9]/g, "");
  if (p.trim().startsWith("+")) return "+" + digits;
  if (digits.startsWith("254")) return "+" + digits;
  if (digits.startsWith("0")) return "+254" + digits.slice(1);
  return "+254" + digits;
};

/* Icons */
const Icon = ({ name, size = 18, color = "currentColor", strokeWidth = 1.8 }) => {
  const paths = {
    Eye:       <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>,
    EyeOff:    <><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><path d="M1 1l22 22"/></>,
    X:         <><path d="M18 6 6 18"/><path d="m6 6 12 12"/></>,
    Phone:     <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.34 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.09a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></>,
    Mail:      <><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></>,
    Briefcase: <><rect width="20" height="14" x="2" y="7" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></>,
    User:      <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* Field */
function Field({ label, type = "text", value, onChange, placeholder, error, leftIcon, autoFocus, inputMode }) {
  const [focused, setFocused] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const isPassword = type === "password";

  return (
    <div style={{ marginBottom: 16 }}>
      {label && (
        <label style={{ fontSize: 12.5, fontWeight: 600, color: "#374151", display: "block", marginBottom: 7, fontFamily: F, letterSpacing: "-0.005em" }}>
          {label}
        </label>
      )}
      <div style={{ position: "relative" }}>
        {leftIcon && (
          <div style={{ position: "absolute", left: 15, top: "50%", transform: "translateY(-50%)", color: focused ? "#16a34a" : "#9ca3af", display: "flex", pointerEvents: "none", transition: "color 0.18s" }}>
            <Icon name={leftIcon} size={16} strokeWidth={1.8} color="inherit" />
          </div>
        )}
        <input
          type={isPassword && showPw ? "text" : type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          autoFocus={autoFocus}
          inputMode={inputMode}
          style={{
            width: "100%", padding: "13px 16px",
            paddingLeft: leftIcon ? 42 : 16,
            borderRadius: 12,
            border: `1.5px solid ${error ? "#ef4444" : focused ? "#16a34a" : "#e7e9ee"}`,
            fontSize: 14.5, outline: "none", fontFamily: F,
            color: "#111827", background: focused ? "#fff" : "#fafbfc",
            boxSizing: "border-box",
            boxShadow: focused
              ? "0 0 0 4px rgba(22,163,74,0.10), inset 0 1px 2px rgba(0,0,0,0.02)"
              : "inset 0 1px 2px rgba(0,0,0,0.02)",
            transition: "border-color 0.18s, box-shadow 0.18s, background 0.18s",
            paddingRight: isPassword ? 46 : 16,
            letterSpacing: "-0.005em",
          }}
        />
        {isPassword && (
          <button type="button" onClick={() => setShowPw(s => !s)}
            style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: 0, display: "flex" }}>
            <Icon name={showPw ? "EyeOff" : "Eye"} size={18} strokeWidth={1.8} />
          </button>
        )}
      </div>
      {error && <p style={{ fontSize: 12, color: "#ef4444", margin: "6px 0 0 2px", fontFamily: F }}>{error}</p>}
    </div>
  );
}

/* Personal / Business toggle */
function AccountToggle({ mode, onChange }) {
  const options = [
    { id: "personal", label: "Personal", icon: "User" },
    { id: "business", label: "Business", icon: "Briefcase" },
  ];
  return (
    <div style={{ display: "inline-flex", background: "#f3f4f6", borderRadius: 12, padding: 4, gap: 4, marginBottom: 22, width: "100%", boxSizing: "border-box" }}>
      {options.map(opt => {
        const active = mode === opt.id;
        return (
          <button key={opt.id} type="button" onClick={() => onChange(opt.id)}
            style={{
              flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
              padding: "10px 14px", borderRadius: 9, border: "none", cursor: "pointer",
              fontFamily: F, fontSize: 13.5, fontWeight: 600, letterSpacing: "-0.005em",
              background: active ? "#fff" : "transparent",
              color: active ? "#111827" : "#9ca3af",
              boxShadow: active ? "0 1px 4px rgba(15,17,23,.08)" : "none",
              transition: "all .18s",
            }}>
            <Icon name={opt.icon} size={14} color={active ? "#16a34a" : "#9ca3af"} strokeWidth={2} />
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/* Error banner */
function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div style={{ background: "rgba(239,68,68,0.07)", border: "1.5px solid rgba(239,68,68,0.18)", borderRadius: 10, padding: "11px 14px", marginBottom: 16 }}>
      <p style={{ fontSize: 12.5, color: "#b91c1c", margin: 0, fontFamily: F, lineHeight: 1.5 }}>{message}</p>
    </div>
  );
}

/* Toast/success banner - for messages passed in via openLogin({ toast }) */
function ToastBanner({ message }) {
  if (!message) return null;
  return (
    <div style={{ background: "rgba(22,163,74,0.08)", border: "1.5px solid rgba(22,163,74,0.22)", borderRadius: 10, padding: "11px 14px", marginBottom: 16 }}>
      <p style={{ fontSize: 12.5, color: "#15803d", margin: 0, fontFamily: F, lineHeight: 1.5 }}>{message}</p>
    </div>
  );
}

export default function LoginModal({ open, onClose, redirectTo, toastMessage }) {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const [mode, setMode]             = useState("personal");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword]     = useState("");
  const [errors, setErrors]         = useState({});
  const [loading, setLoading]       = useState(false);
  const [closing, setClosing]       = useState(false);

  const isEmailMode = mode === "business";

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open && !closing) return null;

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      setIdentifier("");
      setPassword("");
      setErrors({});
      setMode("personal");
      onClose();
    }, 200);
  };

  const handleModeChange = (next) => {
    setMode(next);
    setIdentifier("");
    setErrors({});
  };

  const validate = () => {
    const e = {};
    if (!identifier.trim()) {
      e.identifier = isEmailMode ? "Business email required" : "Phone number required";
    } else if (isEmailMode) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier.trim())) {
        e.identifier = "Enter a valid email address";
      }
    } else {
      if (identifier.replace(/[^0-9]/g, "").length < 9) {
        e.identifier = "Enter a valid phone number";
      }
    }
    if (!password.trim()) e.password = "Password required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setErrors({});

    try {
      const { data, error } = isEmailMode
        ? await supabase.auth.signInWithPassword({ email: identifier.trim(), password })
        : await supabase.auth.signInWithPassword({ phone: normalisePhone(identifier), password });

      if (error) {
        let msg = error.message;
        if (msg.toLowerCase().includes("invalid login credentials") ||
            msg.toLowerCase().includes("invalid credentials")) {
          msg = isEmailMode
            ? "Incorrect email or password."
            : "Incorrect phone number or password. Make sure you've signed up first.";
        } else if (msg.toLowerCase().includes("too many")) {
          msg = "Too many login attempts. Please wait a few minutes and try again.";
        }
        setErrors({ general: msg });
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", data.user.id)
        .maybeSingle();

      handleClose();

      if (redirectTo) {
        navigate(redirectTo);
      } else if (profile?.role === "pro") {
        navigate("/pro/dashboard");
      } else if (profile?.role === "business") {
        navigate("/business/account");
      } else {
        navigate("/");
      }

    } catch (err) {
      setErrors({ general: err.message || "Login failed. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: isMobile ? 0 : 20,
        background: "rgba(8,10,14,0.6)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        animation: closing ? "loginModalFadeOut 0.2s ease forwards" : "loginModalFadeIn 0.25s ease",
      }}>
      <style>{`
        @keyframes loginModalFadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes loginModalFadeOut { from { opacity:1 } to { opacity:0 } }
        @keyframes loginModalRiseIn  { from { opacity:0; transform:translateY(20px) scale(0.97) } to { opacity:1; transform:translateY(0) scale(1) } }
        @keyframes loginModalRiseOut { from { opacity:1; transform:translateY(0) scale(1) } to { opacity:0; transform:translateY(14px) scale(0.97) } }
        @keyframes loginFadeUp { 0% { opacity:0; transform:translateY(10px) } 100% { opacity:1; transform:translateY(0) } }
      `}</style>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: isMobile ? "100%" : "min(940px, 94vw)",
          height: isMobile ? "100%" : "min(680px, 92vh)",
          maxWidth: 940,
          background: "#fff",
          borderRadius: isMobile ? 0 : 28,
          overflow: "hidden",
          display: "flex",
          boxShadow: "0 40px 100px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.06)",
          animation: closing ? "loginModalRiseOut 0.2s ease forwards" : "loginModalRiseIn 0.32s cubic-bezier(0.16,1,0.3,1)",
          position: "relative",
        }}>

        <button onClick={handleClose} style={{
          position: "absolute", top: 18, right: 18, zIndex: 20,
          width: 36, height: 36, borderRadius: "50%",
          background: "rgba(17,24,39,0.06)", border: "none",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", transition: "background 0.15s",
        }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(17,24,39,0.12)"}
          onMouseLeave={e => e.currentTarget.style.background = "rgba(17,24,39,0.06)"}>
          <Icon name="X" size={16} color="#374151" strokeWidth={2.2} />
        </button>

        <div style={{
          width: isMobile ? "100%" : "46%",
          flexShrink: 0,
          display: "flex", flexDirection: "column", justifyContent: "center",
          padding: isMobile ? "48px 32px 36px" : "48px 56px",
          overflow: "hidden",
          boxSizing: "border-box",
        }}>
          <div style={{ marginBottom: 40 }}>
            <AppLogo />
          </div>

          <h1 style={{
            fontFamily: SERIF, fontWeight: 400,
            fontSize: isMobile ? 34 : 40, color: "#111827",
            margin: "0 0 28px", letterSpacing: "-0.02em", lineHeight: 1.05,
          }}>
            Welcome <em style={{ fontStyle: "italic", color: "#16a34a" }}>back</em>
          </h1>

          <AccountToggle mode={mode} onChange={handleModeChange} />

          <form onSubmit={handleSubmit} noValidate key={mode} style={{ animation: "loginFadeUp 0.22s ease" }}>
            <Field
              label={isEmailMode ? "Business email" : "Phone number"}
              placeholder={isEmailMode ? "you@company.com" : "+254 700 000 000"}
              type={isEmailMode ? "email" : "tel"}
              inputMode={isEmailMode ? "email" : "tel"}
              leftIcon={isEmailMode ? "Mail" : "Phone"}
              value={identifier}
              onChange={v => { setIdentifier(v); setErrors(e => ({ ...e, identifier: undefined, general: undefined })); }}
              error={errors.identifier}
              autoFocus
            />
            <Field
              label="Password"
              placeholder="Enter your password"
              type="password"
              value={password}
              onChange={v => { setPassword(v); setErrors(e => ({ ...e, password: undefined, general: undefined })); }}
              error={errors.password}
            />

            <div style={{ display: "flex", justifyContent: "center", marginTop: -6, marginBottom: 18 }}>
              <span
                onClick={() => { handleClose(); navigate("/forgot-password"); }}
                style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 16, color: "#16a34a", cursor: "pointer", letterSpacing: "-0.005em" }}
                onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
                onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}>
                Forgot password?
              </span>
            </div>

            <ToastBanner message={toastMessage} />
            <ErrorBanner message={errors.general} />

            <button type="submit" disabled={loading}
              style={{
                width: "100%", background: "#111827", color: "#fff",
                border: "none", borderRadius: 14, padding: "15px",
                fontSize: 15, fontWeight: 700, cursor: loading ? "default" : "pointer",
                fontFamily: F, transition: "background 0.18s, transform 0.1s",
                opacity: loading ? 0.8 : 1,
                letterSpacing: "0.005em",
                boxShadow: "0 8px 20px rgba(17,24,39,0.18)",
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.background = "#16a34a"; }}
              onMouseLeave={e => { if (!loading) e.currentTarget.style.background = "#111827"; }}
              onMouseDown={e => e.currentTarget.style.transform = "scale(0.985)"}
              onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}>
              {loading ? "Signing in..." : "Login"}
            </button>
          </form>

          <p style={{ fontFamily: SERIF, fontSize: 17, color: "#374151", marginTop: 28, textAlign: isMobile ? "center" : "left", letterSpacing: "-0.005em" }}>
            Not a Jirani yet?&nbsp;&nbsp;
            <span
              onClick={() => { handleClose(); navigate("/signup"); }}
              style={{ fontStyle: "italic", color: "#16a34a", cursor: "pointer" }}
              onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
              onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}>
              Create a free account
            </span>
          </p>
        </div>

        {!isMobile && (
          <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
            <div style={{
              position: "absolute", inset: 0,
              backgroundImage: "url('/images/login-bg.png')",
              backgroundSize: "cover", backgroundPosition: "center top",
              transform: "scale(1.03)",
            }} />
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(8,20,14,0.30) 0%, transparent 22%, transparent 58%, rgba(5,16,11,0.72) 100%)" }} />
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right, rgba(255,255,255,0.05), transparent 16%)" }} />
            <div style={{ position: "absolute", left: 36, right: 36, bottom: 36 }}>
              <div style={{ marginBottom: 10, filter: "drop-shadow(0 1px 8px rgba(0,0,0,0.3))", transform: "scale(0.85)", transformOrigin: "left center" }}>
                <AppLogo variant="light" />
              </div>
              <p style={{
                fontFamily: SERIF, fontWeight: 400, fontStyle: "italic",
                fontSize: 30, lineHeight: 1.18, color: "#fff", margin: 0,
                letterSpacing: "-0.01em", textShadow: "0 4px 24px rgba(0,0,0,0.45)", maxWidth: 320,
              }}>
                Your neighbourhood, connected.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}